// Encapsulation of socket ping behavior. Pings with an initial wait, and
// repeats the ping with multiplied wait time each time we don't hear back.
// Stops after a limit. Uses ActiveTimeouts so that ping waits can be paused.

// Private helpers

const onPingSuccess = function(sp) {
	sp.aTO.clearTimeout(sp.timeoutID);
	sp.currCbs.forEach(f => f());
	sp.reset();
};

const onPingFail = function(sp) {
	sp.currWait *= sp.waitMultiplier;
	sp.sendPing();
};

const notifyStateChange = function(sp) {
	sp.listeners.forEach(f => f(sp.pinging));
};

// Exported class

/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const SocketPing = function(props = {}) {
	if (!(this instanceof SocketPing)) {
		return new SocketPing(props);
	}
	// Required function retrieving current socket object.
	this.getSocket = props.getSocket;
	// Required ActiveTimeouts object.
	this.aTO = props.aTO;

	// Optional properties.
	this.baseWait = props.baseWait || 10000;
	this.waitMultiplier = props.waitMultiplier || 2;
	this.maxWait = props.maxWait || 86400000;

	this.listeners = [];
	this.reset();
};

SocketPing.prototype.isPinging = function() {
	return this.pinging;
};

// When pinging state changes, each listener is called with argument being
// whether we are pinging now. There may be extra calls.
SocketPing.prototype.onStateChange = function(listener) {
	this.listeners.push(listener);
};

SocketPing.prototype.reset = function() {
	this.pinging = false;
	this.currWait = null;
	this.currCbs = null;
	this.aTO.clearTimeout(this.timeoutID);
	this.timeoutID = null;
	notifyStateChange(this);
};

// Noop if not currently pinging.
SocketPing.prototype.sendPing = function() {
	const socket = this.getSocket();
	if (!socket || !this.pinging || this.currWait > this.maxWait) {
		return;
	}
	this.aTO.clearTimeout(this.timeoutID);
	socket.emit("pingSocket");
	this.timeoutID = this.aTO.setTimeout(() => onPingFail(this), this.currWait);
};

SocketPing.prototype.waitOnPing = function(cb) {
	const socket = this.getSocket();
	if (!socket) {
		return;
	} else if (this.pinging) {
		cb && this.currCbs.push(cb);
		return;
	}
	this.pinging = true;
	this.currWait = this.baseWait;
	this.currCbs = cb ? [cb] : [];
	socket.once("pongSocket", () => onPingSuccess(this));
	this.sendPing();
	notifyStateChange(this);
};

module.exports = SocketPing;
